import React, { useState, useEffect, useRef } from 'react';
import { X, Award, User, MapPin, CreditCard, Save, Camera, UploadCloud, Trash2, Loader2 } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getAvatarUrl } from '../../utils/avatar';

const DoctorEditModal = ({ isOpen, onClose, doctor, onSuccess, defaultTab = 'professional' }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('professional');

  // Profile photo state
  const [avatar, setAvatar] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  // Professional details
  const [specialization, setSpecialization] = useState('');
  const [qualification, setQualification] = useState('');
  const [yearOfCompletion, setYearOfCompletion] = useState('');
  const [specializationYear, setSpecializationYear] = useState('');
  const [totalExperience, setTotalExperience] = useState('');
  const [postSpecializationExperience, setPostSpecializationExperience] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [councilName, setCouncilName] = useState('');
  const [workingHospital, setWorkingHospital] = useState('');
  const [hospitalAddress, setHospitalAddress] = useState('');
  const [doctorType, setDoctorType] = useState('Consultant');
  const [specializationBranch, setSpecializationBranch] = useState('');
  const [universityName, setUniversityName] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('Freelance');
  const [bio, setBio] = useState('');

  // Personal details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [alternativePhone, setAlternativePhone] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [emergencyContactNumber, setEmergencyContactNumber] = useState('');
  const [gender, setGender] = useState('Male');
  const [dob, setDob] = useState('');

  // Geographic Address
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [country, setCountry] = useState('India');

  // Payout & Banking
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [upiId, setUpiId] = useState('');
  const [consultationFee, setConsultationFee] = useState('');
  const [expectedChargesPerCase, setExpectedChargesPerCase] = useState('');
  const [showOnWebsite, setShowOnWebsite] = useState(true);

  // Synchronize the active tab when modal is opened
  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab || 'professional');
    }
  }, [isOpen, defaultTab]);

  // Pre-populate fields when doctor prop changes
  useEffect(() => {
    if (doctor) {
      const docAvatar = doctor.user?.avatar || doctor.documents?.find(d => d.type === 'PHOTO')?.url || '';
      setAvatar(docAvatar);
      setAvatarPreview(docAvatar ? getAvatarUrl(docAvatar) : null);

      setShowOnWebsite(doctor.showOnWebsite !== false);
      setSpecialization(doctor.specialization || '');
      setQualification(doctor.primaryQualification || doctor.qualification || '');
      setYearOfCompletion(doctor.yearOfCompletion || doctor.user?.onboardingData?.yearOfCompletion || '');
      setSpecializationYear(doctor.specializationYear || doctor.user?.onboardingData?.specializationYear || '');
      setTotalExperience(doctor.totalExperience !== undefined ? doctor.totalExperience : '');
      setPostSpecializationExperience(doctor.postSpecializationExperience !== undefined ? doctor.postSpecializationExperience : '');
      setRegistrationNumber(doctor.registrationNumber || doctor.user?.onboardingData?.registrationNumber || '');
      setCouncilName(doctor.councilName || doctor.user?.onboardingData?.councilName || '');
      setWorkingHospital(doctor.workingHospital || doctor.user?.onboardingData?.workingHospital || '');
      setHospitalAddress(doctor.hospitalAddress || doctor.user?.onboardingData?.hospitalAddress || '');
      setDoctorType(doctor.doctorType || doctor.user?.onboardingData?.doctorType || 'Consultant');
      setSpecializationBranch(doctor.specializationBranch || doctor.user?.onboardingData?.specializationBranch || '');
      setUniversityName(doctor.universityName || doctor.user?.onboardingData?.universityName || '');
      const rawStatus = doctor.employmentStatus || doctor.user?.onboardingData?.employmentStatus || 'Freelance';
      const normStatus = rawStatus === 'Freelancer' ? 'Freelance' : rawStatus === 'Salaried' ? 'Full-time' : rawStatus;
      setEmploymentStatus(normStatus);
      setBio(doctor.bio || '');

      setFirstName(doctor.user?.firstName || '');
      setLastName(doctor.user?.lastName || '');
      setEmail(doctor.user?.email || '');
      setPhone(doctor.user?.phone || '');
      setAlternativePhone(doctor.user?.alternativePhone || doctor.user?.onboardingData?.alternativePhone || '');
      setEmergencyContact(doctor.user?.onboardingData?.emergencyContact || '');
      setEmergencyContactNumber(doctor.user?.onboardingData?.emergencyContactNumber || doctor.user?.onboardingData?.emergencyContactPersonNumber || '');
      setGender(doctor.user?.gender || doctor.user?.onboardingData?.gender || 'Male');
      setDob(doctor.user?.dob ? new Date(doctor.user.dob).toISOString().split('T')[0] : '');

      setStreet(doctor.user?.address?.street || doctor.user?.onboardingData?.currentAddress || '');
      setCity(doctor.user?.address?.city || doctor.user?.onboardingData?.city || '');
      setState(doctor.user?.address?.state || doctor.user?.onboardingData?.state || '');
      setPincode(doctor.user?.address?.pincode || doctor.user?.onboardingData?.pincode || '');
      setCountry(doctor.user?.address?.country || 'India');

      setBankName(doctor.financials?.bankName || '');
      setAccountNumber(doctor.financials?.accountNumber || '');
      setIfscCode(doctor.financials?.ifscCode || '');
      setPanNumber(doctor.financials?.panNumber || doctor.user?.onboardingData?.panNumber || doctor.user?.panNumber || doctor.panNumber || '');
      setAadhaarNumber(doctor.financials?.aadhaarNumber || doctor.user?.onboardingData?.aadhaarNumber || doctor.user?.aadhaarNumber || doctor.aadhaarNumber || '');
      setUpiId(doctor.financials?.upiId || '');
      setConsultationFee(doctor.financials?.consultationFee !== undefined ? doctor.financials.consultationFee : '');
      setExpectedChargesPerCase(doctor.financials?.expectedChargesPerCase !== undefined ? doctor.financials.expectedChargesPerCase : '');
    }
  }, [doctor, isOpen]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, JPEG, WEBP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Profile photo size should be under 5MB');
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setAvatarPreview(localPreview);
    setUploadingAvatar(true);

    try {
      const upData = new FormData();
      upData.append('documents', file);

      const response = await api.post('/upload', upData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.data?.files?.[0]?.url) {
        const uploadedUrl = response.data.data.files[0].url;
        setAvatar(uploadedUrl);
        setAvatarPreview(uploadedUrl);
        toast.success('Profile photo uploaded! Click "Save Updates" to save.');
      } else {
        throw new Error('Upload failed: no file URL received');
      }
    } catch (error) {
      console.error('Avatar upload failed:', error);
      toast.error(error.response?.data?.message || 'Failed to upload photo');
      setAvatarPreview(avatar ? getAvatarUrl(avatar) : null);
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = () => {
    setAvatar('');
    setAvatarPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Photo removed. Click "Save Updates" to save.');
  };

  if (!isOpen || !doctor) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        specialization,
        qualification,
        yearOfCompletion: yearOfCompletion ? Number(yearOfCompletion) : undefined,
        specializationYear: specializationYear ? Number(specializationYear) : undefined,
        totalExperience: totalExperience !== '' ? Number(totalExperience) : undefined,
        postSpecializationExperience: postSpecializationExperience !== '' ? Number(postSpecializationExperience) : undefined,
        registrationNumber,
        councilName,
        workingHospital,
        hospitalAddress,
        doctorType,
        specializationBranch,
        universityName,
        employmentStatus,
        bio,
        showOnWebsite,
        avatar,
        firstName,
        lastName,
        email,
        phone,
        alternativePhone,
        gender,
        dob: dob || null,
        address: {
          street,
          city,
          state,
          pincode,
          country
        },
        financials: {
          bankName,
          accountNumber,
          ifscCode,
          panNumber,
          aadhaarNumber,
          upiId,
          consultationFee: consultationFee !== '' ? Number(consultationFee) : 0,
          expectedChargesPerCase: expectedChargesPerCase !== '' ? Number(expectedChargesPerCase) : 0
        },
        onboardingData: {
          ...doctor.user?.onboardingData,
          emergencyContact,
          emergencyContactNumber,
          alternativePhone,
          gender
        }
      };

      const response = await api.put(`/doctors/${doctor._id}`, payload);
      if (response.data) {
        toast.success('Doctor profile updated successfully!');
        onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update doctor profile');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'professional', label: 'Professional', icon: Award },
    { id: 'personal', label: 'Personal Details', icon: User },
    { id: 'address', label: 'Geographic Profile', icon: MapPin },
    { id: 'financial', label: 'Banking & Payout', icon: CreditCard }
  ];

  const ActiveIcon = tabs.find(t => t.id === activeTab)?.icon || Award;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <div className="bg-white w-full max-w-5xl h-[95vh] md:h-[90vh] overflow-hidden rounded-[32px] md:rounded-[40px] shadow-2xl flex flex-col relative animate-in fade-in zoom-in duration-300">

        {/* Header */}
        <div className="px-6 py-5 md:px-10 md:py-6 border-b border-slate-100 flex items-center justify-between bg-white relative z-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shadow-inner shrink-0">
              <ActiveIcon className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-black text-[#143E78] tracking-tight uppercase">
                Edit Doctor Profile
              </h2>
              <p className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">
                Update professional, personal, address & payment settings
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full transition-all text-slate-400">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="px-6 md:px-10 bg-slate-50 border-b border-slate-100 flex gap-2 overflow-x-auto py-2 shrink-0">
          {tabs.map(tab => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-[10px] md:text-xs font-black uppercase tracking-wider rounded-xl transition-all ${isActive ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200/50 hover:text-slate-700'
                  }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Form content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-10 md:py-8 space-y-6">

          {/* PROFESSIONAL TAB */}
          {activeTab === 'professional' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Specialization (Degree)</label>
                  <input
                    type="text"
                    required
                    value={specialization}
                    onChange={(e) => setSpecialization(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Qualification</label>
                  <input
                    type="text"
                    required
                    value={qualification}
                    onChange={(e) => setQualification(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Specialization Branch</label>
                  <input
                    type="text"
                    value={specializationBranch}
                    onChange={(e) => setSpecializationBranch(e.target.value)}
                    placeholder="e.g. Cardiology, Orthopedics"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Year of Completion</label>
                  <input
                    type="number"
                    value={yearOfCompletion}
                    onChange={(e) => setYearOfCompletion(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Specialization Year</label>
                  <input
                    type="number"
                    value={specializationYear}
                    onChange={(e) => setSpecializationYear(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">University Name</label>
                  <input
                    type="text"
                    value={universityName}
                    onChange={(e) => setUniversityName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Total Experience (Years)</label>
                  <input
                    type="number"
                    value={totalExperience}
                    onChange={(e) => setTotalExperience(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Post-Specialization Exp (Years)</label>
                  <input
                    type="number"
                    value={postSpecializationExperience}
                    onChange={(e) => setPostSpecializationExperience(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Doctor Type</label>
                  <select
                    value={doctorType}
                    onChange={(e) => setDoctorType(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  >
                    <option value="Consultant">Consultant</option>
                    <option value="Resident">Resident</option>
                    <option value="Surgeon">Surgeon</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Registration Number</label>
                  <input
                    type="text"
                    required
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Council Name</label>
                  <input
                    type="text"
                    value={councilName}
                    onChange={(e) => setCouncilName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Employment Status</label>
                  <select
                    value={employmentStatus}
                    onChange={(e) => setEmploymentStatus(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  >
                    <option value="Freelance">Freelance</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Visiting Consultant">Visiting Consultant</option>
                  </select>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Working Hospital/Clinic Name</label>
                  <input
                    type="text"
                    value={workingHospital}
                    onChange={(e) => setWorkingHospital(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1 md:col-span-3">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Working Hospital/Clinic Address</label>
                  <input
                    type="text"
                    value={hospitalAddress}
                    onChange={(e) => setHospitalAddress(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1 md:col-span-3">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Bio (About Doctor)</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all min-h-[80px]"
                  />
                </div>

                <div className="md:col-span-3 p-4 bg-slate-50 rounded-2xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black text-[#143E78] uppercase tracking-wider">Show Profile on Public Website</p>
                    <p className="text-xs text-slate-500 mt-0.5">Allow this doctor to appear on public website service details pages matching their specialization.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showOnWebsite}
                      onChange={(e) => setShowOnWebsite(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* PERSONAL TAB */}
          {activeTab === 'personal' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              
              {/* Profile Photo Upload Section */}
              <div className="p-4 md:p-5 bg-gradient-to-r from-slate-50 via-indigo-50/40 to-blue-50/20 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col sm:flex-row items-center gap-5">
                {/* Avatar Preview */}
                <div className="relative group shrink-0">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white border-2 border-indigo-200 overflow-hidden shadow-md flex items-center justify-center relative">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Doctor Avatar"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.parentNode.querySelector('.modal-avatar-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`modal-avatar-fallback w-full h-full items-center justify-center bg-indigo-50 text-indigo-600 font-black text-xl md:text-2xl ${
                        avatarPreview ? 'hidden' : 'flex'
                      }`}
                    >
                      {(firstName?.[0] || doctor.user?.firstName?.[0] || 'D').toUpperCase()}
                      {(lastName?.[0] || doctor.user?.lastName?.[0] || 'R').toUpperCase()}
                    </div>

                    {uploadingAvatar && (
                      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xs flex flex-col items-center justify-center text-white z-10">
                        <Loader2 className="w-6 h-6 animate-spin text-white mb-1" />
                        <span className="text-[9px] font-bold tracking-wider uppercase">Uploading...</span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    title="Choose photo"
                    className="absolute bottom-0 right-0 p-2 bg-[#143E78] hover:bg-[#1C56A3] text-white rounded-full shadow-lg border-2 border-white transition-all transform hover:scale-110 disabled:opacity-50"
                  >
                    <Camera className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Upload controls */}
                <div className="flex-1 text-center sm:text-left space-y-1.5 w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-black text-[#143E78] uppercase tracking-wide">
                        Doctor Profile Photo
                      </h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Displayed on public website specialist cards, consultation bookings, and CRM.
                      </p>
                    </div>

                    <div className="flex items-center justify-center sm:justify-end gap-2 shrink-0">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />

                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAvatar}
                        className="px-3.5 py-2 bg-[#143E78] hover:bg-[#1C56A3] text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                      >
                        {uploadingAvatar ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <UploadCloud className="w-3.5 h-3.5" />
                            <span>{avatar ? 'Change Photo' : 'Upload Photo'}</span>
                          </>
                        )}
                      </button>

                      {avatar && (
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          disabled={uploadingAvatar}
                          className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 text-xs font-bold rounded-xl transition-all flex items-center gap-1 disabled:opacity-50 cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Remove</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-400 font-medium">
                    Allowed formats: PNG, JPG, JPEG, WEBP. Max size: 5MB.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Phone Number</label>
                  <input
                    type="text"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Alternative Phone</label>
                  <input
                    type="text"
                    value={alternativePhone}
                    onChange={(e) => setAlternativePhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Emergency Contact Person</label>
                  <input
                    type="text"
                    value={emergencyContact}
                    onChange={(e) => setEmergencyContact(e.target.value)}
                    placeholder="Name of emergency contact"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Emergency Contact Number</label>
                  <input
                    type="text"
                    value={emergencyContactNumber}
                    onChange={(e) => setEmergencyContactNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit mobile number"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Gender</label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Date of Birth</label>
                  <input
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all cursor-pointer"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ADDRESS TAB */}
          {activeTab === 'address' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Street Address</label>
                  <input
                    type="text"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">State</label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Pincode</label>
                  <input
                    type="text"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Country</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          )}

          {/* FINANCIAL TAB */}
          {activeTab === 'financial' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2">KYC Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">PAN Number</label>
                  <input
                    type="text"
                    value={panNumber}
                    onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Aadhaar Number</label>
                  <input
                    type="text"
                    value={aadhaarNumber}
                    onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>

              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2 mt-8">Payout Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Consultation Fee (₹)</label>
                  <input
                    type="number"
                    value={consultationFee}
                    onChange={(e) => setConsultationFee(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Expected Charges per Case (₹)</label>
                  <input
                    type="number"
                    value={expectedChargesPerCase}
                    onChange={(e) => setExpectedChargesPerCase(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">UPI ID</label>
                  <input
                    type="text"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="e.g. name@upi"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>

              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2 mt-8">Bank Account details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Bank Name</label>
                  <input
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Account Number</label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">IFSC Code</label>
                  <input
                    type="text"
                    value={ifscCode}
                    onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-5 md:px-10 md:py-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-[32px] md:rounded-b-[40px] shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || uploadingAvatar}
            className="px-6 py-2.5 bg-[#143E78] text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-indigo-900 transition-all shadow-lg active:scale-95 flex items-center gap-2 disabled:opacity-50 cursor-pointer"
          >
            {loading || uploadingAvatar ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {loading ? 'Saving...' : uploadingAvatar ? 'Uploading Photo...' : 'Save Updates'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default DoctorEditModal;
